import React, { useState, useEffect } from "react";
import styles from "./EditProfileForm.module.css";
import { useFormHandler, validationRules } from "../hooks/useFormHandler";
import { INDIAN_STATES } from "../constants/states";
import { INTERESTED_FIELDS_OPTIONS } from "../constants/interestedFields";
import { useDeleteMediaMutation } from "../store/api/profileApi";
import { toast } from "react-toastify";
import { LOOKING_FOR_OPTIONS } from "../constants/lookingFor";
const EditProfileForm = ({ user, onSubmit, isSubmitting, onCancel }) => {
  const [photoPreview, setPhotoPreview] = useState(
    user?.profilePhotoUrl || null,
  );

  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [stateSearch, setStateSearch] = useState(user?.state || "");

  const [showFieldsDropdown, setShowFieldsDropdown] = useState(false);
  const [fieldSearch, setFieldSearch] = useState("");

  const [showLookingDropdown, setShowLookingDropdown] = useState(false);
  const [lookingSearch, setLookingSearch] = useState("");
  const [otherLookingFor, setOtherLookingFor] = useState("");

  const [deleteMedia, { isLoading: isDeletingPhoto }] =
    useDeleteMediaMutation();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [otherUserType, setOtherUserType] = useState("");
  const [otherInterestedFields, setOtherInterestedFields] = useState([]);
  const [currentOtherField, setCurrentOtherField] = useState("");
  const [isTypingCustomField, setIsTypingCustomField] = useState(false);
  const confirmRemovePhoto = async () => {
    try {
      // CASE 1: newly selected image (not saved in DB yet)
      if (photoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview);

        setPhotoPreview(null);

        setFormValues({
          ...formData,
          ProfilePhoto: null,
        });

        setShowDeleteConfirm(false);

        toast.info("Photo removed");
        return;
      }

      // CASE 2: existing saved photo
      await deleteMedia("photo").unwrap();

      setPhotoPreview(null);

      setFormValues({
        ...formData,
        ProfilePhoto: null,
      });

      setShowDeleteConfirm(false);

      toast.success("Profile photo removed");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to remove photo");
    }
  };

  const filteredStates = INDIAN_STATES.filter((s) =>
    s.toLowerCase().includes(stateSearch.toLowerCase()),
  );

  const filteredFields = INTERESTED_FIELDS_OPTIONS.filter((f) =>
    f.toLowerCase().includes(fieldSearch.toLowerCase()),
  );

  const formValidationRules = {
    Name: [validationRules.required("Full Name")],
    State: [validationRules.required("State")],
    UserType: [validationRules.required("User Type")],
    InterestedFields: [
      validationRules.maxLength(
        200,
        "Interested Fields must be less than 200 characters",
      ),
    ],
  };

  const {
    formData,
    errors,
    touched,
    handleChange: baseHandleChange,
    handleBlur,
    validateForm,
    setFormValues,
    setErrors,
  } = useFormHandler(
    {
      ProfilePhoto: null,
      Name: user?.name || "",
      DateOfBirth: user?.dateOfBirth ? user.dateOfBirth.split("T")[0] : "",
      ContactNumber:
        user?.contactNumber && user.contactNumber !== "null"
          ? user.contactNumber
          : "",
      Gender: user?.gender || "",
      UserType: user?.userType || "",
      RookieType: user?.rookieType || "",
      Bio: user?.bio || "",
      State: user?.state || "",
      City: user?.city || "",
      InterestedFields: user?.interestedFields
        ? user.interestedFields.split(",").map((s) => s.trim())
        : [],
      LookingFor: user?.lookingFor
        ? user.lookingFor.split(",").map((s) => s.trim())
        : [],
      Video: null,
    },
    formValidationRules,
  );

  // Sync when user prop changes
  useEffect(() => {
    if (user) {
      setFormValues({
        Name: user.name || "",
        DateOfBirth: user.dateOfBirth ? user.dateOfBirth.split("T")[0] : "",
        ContactNumber:
          user.contactNumber && user.contactNumber !== "null"
            ? user.contactNumber
            : "",
        Gender: user.gender || "",
        UserType: user.userType || "",
        RookieType: user.rookieType || "",
        Bio: user.bio || "",
        State: user.state || "",
        City: user.city || "",
        InterestedFields: user.interestedFields
          ? user.interestedFields.split(",").map((s) => s.trim())
          : [],
        LookingFor: user?.lookingFor
          ? user.lookingFor.split(",").map((s) => s.trim())
          : [],
      });
      setStateSearch(user.state || "");

      // preload custom values
      if (
        user.userType &&
        ![
          "Rookie",
          "Entrepreneur",
          "Business Professional",
          "Investor",
          "Mentor",
        ].includes(user.userType)
      ) {
        setOtherUserType(user.userType);

        setFormValues((prev) => ({
          ...prev,
          UserType: "Others",
        }));
      }

      if (user.interestedFields) {
        const fields = user.interestedFields.split(",").map((x) => x.trim());

        const customField = fields.find(
          (x) => !INTERESTED_FIELDS_OPTIONS.includes(x),
        );

        if (customField) {
          setFormValues((prev) => ({
            ...prev,

            InterestedFields: [
              ...fields.filter((x) => x !== customField),

              "Others",
            ],
          }));
        } else {
          setFormValues((prev) => ({
            ...prev,

            InterestedFields: fields,
          }));
        }
      }

      if (user?.lookingFor) {
        const values = user.lookingFor.split(",").map((x) => x.trim());

        const customValue = values.find(
          (x) => !LOOKING_FOR_OPTIONS.includes(x),
        );

        if (customValue) {
          setOtherLookingFor(customValue);

          setFormValues((prev) => ({
            ...prev,
            LookingFor: [...values.filter((x) => x !== customValue), "Others"],
          }));
        }
      }
    }
  }, [user, setFormValues]);

  // Close dropdowns on outside click
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(`.${styles.dropdownWrapper}`)) {
        setShowStateDropdown(false);
        setShowFieldsDropdown(false);
        setShowLookingDropdown(false);
      }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        ProfilePhoto: "Please upload a valid image file",
      }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        ProfilePhoto: "File size must be less than 5MB",
      }));
      return;
    }
    setPhotoPreview(URL.createObjectURL(file));
    baseHandleChange(e);
    setErrors((prev) => ({ ...prev, ProfilePhoto: "" }));
  };

  const toggleField = (value) => {
    let current = [...(formData.InterestedFields || [])];

    // Multiple Others allowed
    if (value === "Others") {
      const total =
        current.filter((x) => x !== "Others").length +
        otherInterestedFields.length;

      if (total >= 5) return;

      current.push("Others");

      setCurrentOtherField("");
      setShowFieldsDropdown(false);
    } else {
      if (current.includes(value)) {
        current = current.filter((v) => v !== value);
      } else {
        const total =
          current.filter((x) => x !== "Others").length +
          otherInterestedFields.length;

        if (total >= 5) return;

        current.push(value);
      }
    }

    setFormValues({
      ...formData,
      InterestedFields: current,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const fd = new FormData();
    const fieldMap = {
      Name: "Name",
      DateOfBirth: "DateOfBirth",
      ContactNumber: "ContactNumber",
      Gender: "Gender",
      UserType: "UserType",
      RookieType: "RookieType",
      Bio: "Bio",
      State: "State",
      City: "City",
    };

    Object.entries(fieldMap).forEach(([key, apiKey]) => {
      let value = formData[key];

      if (key === "UserType" && value === "Others") {
        value = otherUserType.trim() || "Others";
      }

      if (key === "Bio") {
        fd.append(apiKey, value ?? "");
      } else if (value !== null && value !== "") {
        fd.append(apiKey, value);
      }
    });

    let finalInterested = [...(formData.InterestedFields || [])];

    finalInterested = [
      ...finalInterested.filter((x) => x !== "Others"),
      ...otherInterestedFields,
    ];

    fd.append("InterestedFields", finalInterested.join(","));
    if (formData.ProfilePhoto instanceof File)
      fd.append("ProfilePhoto", formData.ProfilePhoto);

    const finalLookingFor = formData.LookingFor?.includes("Others")
      ? formData.LookingFor.map((item) =>
          item === "Others" ? otherLookingFor.trim() : item,
        )
      : formData.LookingFor || [];

    fd.append("LookingFor", finalLookingFor.join(","));

    onSubmit(fd);
  };

  const toggleLooking = (value) => {
    let current = [...(formData.LookingFor || [])];

    if (current.includes(value)) {
      current = current.filter((v) => v !== value);

      if (value === "Others") {
        setOtherLookingFor("");
      }
    } else {
      if (current.length >= 5) return;

      current.push(value);
    }

    setFormValues({
      ...formData,
      LookingFor: current,
    });
  };

  const filteredLooking = LOOKING_FOR_OPTIONS.filter((item) =>
    item.toLowerCase().includes(lookingSearch.toLowerCase()),
  );

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {/* ── Photo ── */}
      <div className={styles.formGroup}>
        <div className={styles.photoSection}>
          <label htmlFor="profilePhoto" className={styles.photoLabel}>
            <div className={styles.photoPreview}>
              <div className={styles.photoPreview}>
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile Preview" />
                ) : (
                  <div className={styles.placeholder}>No Photo</div>
                )}

                {/* overlay */}
                <div className={styles.photoOverlay}>
                  <label htmlFor="profilePhoto" className={styles.editBtn}>
                    ✏️
                  </label>

                  {photoPreview && !photoPreview.startsWith("blob:") && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className={styles.deleteBtn}
                      disabled={isDeletingPhoto}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            </div>
          </label>
          <input
            type="file"
            accept="image/*"
            id="profilePhoto"
            name="ProfilePhoto"
            onChange={handlePhotoChange}
            className={styles.hiddenFileInput}
          />
          {errors.ProfilePhoto && (
            <span className={styles.error}>{errors.ProfilePhoto}</span>
          )}
        </div>
      </div>

      {/* ── Name & DOB ── */}
      <div className={styles.row}>
        <div className={styles.fieldGroup}>
          <label htmlFor="Name" className={styles.label}>
            Full Name <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            id="Name"
            name="Name"
            placeholder="Full Name"
            value={formData.Name}
            onChange={baseHandleChange}
            onBlur={handleBlur}
            className={styles.input}
          />
          {touched.Name && errors.Name && (
            <span className={styles.error}>{errors.Name}</span>
          )}
        </div>

        <div className={styles.fieldGroup}>
          <label htmlFor="DateOfBirth" className={styles.label}>
            Date of Birth
          </label>
          <input
            type="date"
            id="DateOfBirth"
            name="DateOfBirth"
            value={formData.DateOfBirth}
            onChange={baseHandleChange}
            onBlur={handleBlur}
            className={styles.input}
          />
        </div>
      </div>

      {/* ── Phone & Gender ── */}
      <div className={styles.row}>
        <div className={styles.fieldGroup}>
          <label htmlFor="ContactNumber" className={styles.label}>
            Mobile Number
          </label>
          <input
            type="tel"
            id="ContactNumber"
            name="ContactNumber"
            placeholder="10-digit number"
            value={formData.ContactNumber}
            onChange={baseHandleChange}
            onBlur={handleBlur}
            className={styles.input}
          />
          {touched.ContactNumber && errors.ContactNumber && (
            <span className={styles.error}>{errors.ContactNumber}</span>
          )}
        </div>

        <div className={styles.fieldGroup}>
          <label htmlFor="Gender" className={styles.label}>
            Gender
          </label>
          <select
            id="Gender"
            name="Gender"
            value={formData.Gender}
            onChange={baseHandleChange}
            onBlur={handleBlur}
            className={styles.input}
          >
            <option value="" disabled hidden>
              Select Gender
            </option>

            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      {/* ── State (searchable dropdown) & City ── */}
      <div className={styles.row}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            State <span className={styles.required}>*</span>
          </label>
          <div className={styles.dropdownWrapper}>
            <div
              className={`${styles.multiInput} ${showStateDropdown ? styles.multiInputOpen : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowStateDropdown(true);
              }}
            >
              <input
                className={styles.searchInput}
                type="text"
                placeholder="Search state…"
                value={stateSearch}
                onChange={(e) => {
                  setStateSearch(e.target.value);
                  setShowStateDropdown(true);
                  setFormValues({ ...formData, State: "" });
                }}
              />
            </div>
            <span
              className={`${styles.dropdownCaret} ${showStateDropdown ? styles.dropdownCaretOpen : ""}`}
            />

            {showStateDropdown && (
              <div className={styles.dropdownList}>
                {filteredStates.length > 0 ? (
                  filteredStates.map((state) => (
                    <div
                      key={state}
                      className={styles.dropdownItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormValues({ ...formData, State: state });
                        setStateSearch(state);
                        setShowStateDropdown(false);
                      }}
                    >
                      {state}
                    </div>
                  ))
                ) : (
                  <div className={styles.noResult}>No states found</div>
                )}
              </div>
            )}
          </div>
          {touched.State && errors.State && (
            <span className={styles.error}>{errors.State}</span>
          )}
        </div>

        <div className={styles.fieldGroup}>
          <label htmlFor="City" className={styles.label}>
            City
          </label>
          <input
            type="text"
            id="City"
            name="City"
            placeholder="City"
            value={formData.City}
            onChange={baseHandleChange}
            onBlur={handleBlur}
            className={styles.input}
          />
        </div>
      </div>

      {/* ── User Type ── */}
      <div className={styles.formGroup}>
        <label htmlFor="UserType" className={styles.label}>
          User Type <span className={styles.required}>*</span>
        </label>
        <select
          id="UserType"
          name="UserType"
          value={formData.UserType}
          onChange={(e) => {
            baseHandleChange(e);

            if (e.target.value !== "Others") {
              setOtherUserType("");
            }
          }}
          onBlur={handleBlur}
          className={styles.input}
        >
          <option value="">Select User Type</option>
          <option value="Rookie">Rookie</option>
          <option value="Entrepreneur">Entrepreneur</option>
          <option value="Business Professional">Business Professional</option>
          <option value="Investor">Investor</option>
          <option value="Freelancer">Freelancer</option>
          <option value="Mentor">Mentor</option>
          <option value="Others">Others</option>
        </select>
        {formData.UserType === "Others" && (
          <input
            type="text"
            placeholder="Enter user type"
            className={styles.input}
            value={otherUserType}
            onChange={(e) => setOtherUserType(e.target.value)}
          />
        )}
        {touched.UserType && errors.UserType && (
          <span className={styles.error}>{errors.UserType}</span>
        )}
      </div>

      {/* ── Rookie Type ── */}
      {formData.UserType === "Rookie" && (
        <div className={styles.formGroup}>
          <label htmlFor="RookieType" className={styles.label}>
            Role
          </label>
          <select
            id="RookieType"
            name="RookieType"
            value={formData.RookieType}
            onChange={baseHandleChange}
            className={styles.input}
          >
            <option value="">Select one</option>
            <option value="Student">Student</option>
            <option value="Working Professional">Working Professional</option>
            <option value="Others">Others</option>
          </select>
        </div>
      )}

      {/* ── Interested Fields (multi-select) ── */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Interested Fields</label>
        <div className={styles.dropdownWrapper}>
          <div
            className={`${styles.multiInput} ${showFieldsDropdown ? styles.multiInputOpen : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowFieldsDropdown(true);
            }}
          >
            {[
              ...formData.InterestedFields.filter((x) => x !== "Others"),
              ...otherInterestedFields,
            ].map((f, index) => (
              <span key={index} className={styles.tag}>
                {f}

                <span
                  className={styles.removeTag}
                  onClick={(e) => {
                    e.stopPropagation();

                    if (otherInterestedFields.includes(f)) {
                      setOtherInterestedFields(
                        otherInterestedFields.filter((x) => x !== f),
                      );
                    } else {
                      toggleField(f);
                    }
                  }}
                >
                  ×
                </span>
              </span>
            ))}
            <input
              className={styles.searchInput}
              type="text"
              placeholder={
                formData.InterestedFields.length ? "" : "Select up to 5 fields…"
              }
              value={fieldSearch}
              onChange={(e) => {
                setFieldSearch(e.target.value);
                setShowFieldsDropdown(true);
              }}
            />
          </div>
          <span
            className={`${styles.dropdownCaret} ${showFieldsDropdown ? styles.dropdownCaretOpen : ""}`}
          />

          {showFieldsDropdown && (
            <div className={styles.dropdownList}>
              {filteredFields.length > 0 ? (
                filteredFields.map((f) => (
                  <div
                    key={f}
                    className={`${styles.dropdownItem} ${formData.InterestedFields.includes(f) ? styles.selectedItem : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleField(f);
                    }}
                  >
                    {f}
                  </div>
                ))
              ) : (
                <div className={styles.noResult}>No results found</div>
              )}
            </div>
          )}
        </div>
        {formData.InterestedFields.includes("Others") && (
          <div>
            <input
              type="text"
              placeholder="Enter custom field"
              className={styles.input}
              value={currentOtherField}
              onChange={(e) => setCurrentOtherField(e.target.value)}
            />

            <button
              type="button"
              className={styles.submitBtn}
              style={{ marginTop: "8px" }}
              onClick={() => {
                const value = currentOtherField.trim();

                if (!value) return;

                if (
                  otherInterestedFields.length +
                    formData.InterestedFields.filter((x) => x !== "Others")
                      .length >=
                  5
                )
                  return;

                setOtherInterestedFields([...otherInterestedFields, value]);

                setCurrentOtherField("");
              }}
            >
              Add
            </button>
          </div>
        )}
        <span className={styles.fieldHint}>
          {formData.InterestedFields.filter((x) => x !== "Others").length +
            otherInterestedFields.length}
          /5 selected
        </span>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Looking For</label>

        <div className={styles.dropdownWrapper}>
          <div
            className={`${styles.multiInput} ${
              showLookingDropdown ? styles.multiInputOpen : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setShowLookingDropdown(true);
            }}
          >
            {(formData.LookingFor || []).map((item) => (
              <span key={item} className={styles.tag}>
                {item === "Others" ? otherLookingFor || "Others" : item}

                <span
                  className={styles.removeTag}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLooking(item);
                  }}
                >
                  ×
                </span>
              </span>
            ))}

            <input
              className={styles.searchInput}
              type="text"
              placeholder="Select options..."
              value={lookingSearch}
              onChange={(e) => {
                setLookingSearch(e.target.value);
                setShowLookingDropdown(true);
              }}
            />
          </div>

          {showLookingDropdown && (
            <div className={styles.dropdownList}>
              {filteredLooking.map((option) => (
                <div
                  key={option}
                  className={`${styles.dropdownItem} ${
                    formData.LookingFor?.includes(option)
                      ? styles.selectedItem
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLooking(option);
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>

        {formData.LookingFor?.includes("Others") && (
          <input
            type="text"
            placeholder="Enter what you're looking for"
            value={otherLookingFor}
            onChange={(e) => setOtherLookingFor(e.target.value)}
            className={styles.input}
            style={{ marginTop: "8px" }}
          />
        )}

        <span className={styles.fieldHint}>
          {(formData.LookingFor || []).length}/5 selected
        </span>
      </div>

      {/* ── Bio ── */}
      <div className={styles.formGroup}>
        <label htmlFor="Bio" className={styles.label}>
          Bio
        </label>
        <textarea
          id="Bio"
          name="Bio"
          placeholder="Tell us about yourself…"
          value={formData.Bio}
          onChange={baseHandleChange}
          onBlur={handleBlur}
          className={styles.textarea}
          rows={4}
        />
      </div>

      {/* ── Actions ── */}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={styles.submitBtn}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {showDeleteConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmBox}>
            <div className={styles.confirmIcon}>🗑️</div>

            <h3 className={styles.confirmTitle}>Remove profile photo?</h3>

            <p className={styles.confirmText}>
              This action will permanently delete your profile picture.
            </p>

            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmCancel}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className={styles.confirmDelete}
                onClick={confirmRemovePhoto}
                disabled={isDeletingPhoto}
              >
                {isDeletingPhoto ? "Removing..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default React.memo(EditProfileForm);
